import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminWorkflow, workflowError, localDateTime, displayDate } from '../utils/electionWorkflows';
import WorkflowPagination from './WorkflowPagination';

export default function ElectionNominations({ election, onCandidatesChanged }) {
  const [settings, setSettings] = useState({ enabled: false, opensAt: '', closesAt: '' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [detailPage, setDetailPage] = useState(1);
  const [details, setDetails] = useState(null);
  const base = `${election.id}/nominations`;
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminWorkflow('get', `${election.id}/nominations?page=${page}`);
      setData(response.data.data);
    } catch (err) { setError(workflowError(err)); }
    finally { setLoading(false); }
  }, [election.id, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    let active = true;
    adminWorkflow('get', `${election.id}/nominations/settings`).then(response => {
      if (active) {
        setSettings({
          enabled: response.data.data.enabled,
          opensAt: localDateTime(response.data.data.opensAt), closesAt: localDateTime(response.data.data.closesAt),
        });
        setSettingsLoaded(true);
      }
    }).catch(err => { if (active) setError(workflowError(err)); });
    return () => { active = false; };
  }, [election.id]);
  useEffect(() => {
    if (!selected) return;
    let active = true;
    setDetails(null);
    adminWorkflow('get', `${election.id}/nominees/${selected.id}?page=${detailPage}`)
      .then(response => { if (active) setDetails(response.data.data); })
      .catch(err => { if (active) setError(workflowError(err)); });
    return () => { active = false; };
  }, [election.id, selected, detailPage]);
  const save = async event => {
    event.preventDefault();
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const response = await adminWorkflow('put', `${base}/settings`, {
        enabled: settings.enabled,
        opensAt: settings.opensAt ? new Date(settings.opensAt).toISOString() : null,
        closesAt: settings.closesAt ? new Date(settings.closesAt).toISOString() : null,
      });
      setMessage(response.data.message);
    } catch (err) { setError(workflowError(err)); }
    finally { setBusy(false); }
  };
  const review = async (nominee, status) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const response = await adminWorkflow('patch', `${election.id}/nominees/${nominee.id}`, { status });
      setMessage(response.data.message);
      await load();
      if (status === 'approved') onCandidatesChanged();
    } catch (err) { setError(workflowError(err)); }
    finally { setBusy(false); }
  };
  const url = `${window.location.origin}/nominations/${election.id}`;
  return <details className="card mb-4">
    <summary className="card-header fw-bold p-3">Candidate nominations {data && `(${data.totalConfirmed} confirmed)`}</summary>
    <div className="card-body">
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      {message && <div className="alert alert-info" role="status">{message}</div>}
      <p>Allow anyone with the nomination link to propose someone or endorse an existing candidate.
        Only email-confirmed nominations count. These are separate from election votes.</p>
      <form onSubmit={save} className="mb-4">
        <div className="form-check mb-3">
          <input className="form-check-input" id="nominations-enabled" type="checkbox" checked={settings.enabled}
            onChange={event => setSettings({ ...settings, enabled: event.target.checked })} />
          <label className="form-check-label" htmlFor="nominations-enabled">Enable public nominations</label>
        </div>
        {election.accessType === 'closed' && <p className="text-warning">
          Enabling this publishes the election name, position names, and approved candidate names to anyone with the nomination link.
          It does not open voting or publish contact details.
        </p>}
        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label" htmlFor="nom-opens">Nominations open (local time)</label>
            <input id="nom-opens" className="form-control" type="datetime-local" value={settings.opensAt} required={settings.enabled}
              onChange={event => setSettings({ ...settings, opensAt: event.target.value })} />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label" htmlFor="nom-closes">Nominations close (local time)</label>
            <input id="nom-closes" className="form-control" type="datetime-local" value={settings.closesAt} required={settings.enabled}
              onChange={event => setSettings({ ...settings, closesAt: event.target.value })} />
          </div>
        </div>
        <button className="btn btn-primary" disabled={busy || !settingsLoaded}>Save nomination settings</button>
      </form>
      <label className="form-label" htmlFor="nom-link">Public nomination link (works only during the enabled window)</label>
      <div className="input-group mb-4">
        <input id="nom-link" className="form-control" value={url} readOnly />
        <button className="btn btn-outline-primary" onClick={async () => {
          try { await navigator.clipboard.writeText(url); setMessage('Nomination link copied'); }
          catch (_) { setError('Could not copy. Select and copy the link manually.'); }
        }}>Copy link</button>
      </div>
      <div className="d-flex justify-content-between mb-3">
        <strong>{data?.totalNominees || 0} nominees · {data?.totalConfirmed || 0} confirmed nominations</strong>
        <button className="btn btn-sm btn-outline-secondary" disabled={loading || busy} onClick={load}>Refresh counts</button>
      </div>
      {data && <div className="d-flex flex-wrap gap-2 mb-3">
        {election.positions?.map(position => <span className="badge bg-light text-dark" key={position.id}>
          {position.name}: {data.byPosition[position.id] || 0}
        </span>)}
      </div>}
      {loading && <p role="status">Loading nominations...</p>}
      <div className="table-responsive">
        <table className="table align-middle">
          <thead><tr><th>Nominee</th><th>Position</th><th>Confirmed</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>{data?.nominees.map(nominee => <tr key={nominee.id}>
            <td>{nominee.fullName}<small className="d-block text-muted">{nominee.email}</small></td>
            <td>{election.positions?.find(position => position.id === nominee.positionId)?.name || 'Removed position'}</td>
            <td>{nominee.nominationCount}</td><td>{nominee.status}</td>
            <td><div className="d-flex flex-wrap gap-2">
              <button className="btn btn-sm btn-outline-primary" onClick={() => { setSelected(nominee); setDetailPage(1); }}>Details</button>
              {(nominee.status !== 'approved' || !nominee.candidateId) && <button className="btn btn-sm btn-success"
                disabled={busy || nominee.nominationCount === 0} onClick={() => review(nominee, 'approved')}>Approve as candidate</button>}
              {!nominee.candidateId && nominee.status !== 'rejected' && <button className="btn btn-sm btn-outline-danger"
                disabled={busy} onClick={() => review(nominee, 'rejected')}>Reject</button>}
              {nominee.candidateId && <Link className="btn btn-sm btn-outline-secondary" to={`/admin/positions/${nominee.positionId}/candidates`}>Manage candidate</Link>}
            </div></td>
          </tr>)}</tbody>
        </table>
      </div>
      {data && !data.nominees.length && <p>No nominations received yet.</p>}
      <WorkflowPagination page={page} total={data?.totalNominees || 0} onChange={setPage} disabled={loading} />
      {selected && <section className="border rounded p-3 mt-3">
        <div className="d-flex justify-content-between">
          <h3 className="h6">Nominations for {selected.fullName}</h3>
          <button className="btn btn-sm btn-link" onClick={() => setSelected(null)}>Close details</button>
        </div>
        {!details ? <p>Loading nomination details...</p> : <>
          {details.nominations.map(item => <div className="border-bottom py-2" key={item.id}>
            <strong>{item.nominatorName}</strong> ({item.nominatorEmail})
            <div className="small text-muted">{item.confirmedAt ? `Confirmed ${displayDate(item.confirmedAt)}` : 'Awaiting email confirmation'}</div>
            <p className="mb-1" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{item.reason || 'No reason provided'}</p>
          </div>)}
          <WorkflowPagination page={detailPage} total={details.total} onChange={setDetailPage} />
        </>}
      </section>}
    </div>
  </details>;
}
